class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.byobProductId;
    this.productId16 = this.dataset.byobProductId16;
    this.maxItems = parseInt(this.dataset.maxItems, 10) || 4;
    this.items = [];
  }

  connectedCallback() {
    this.fetchSkioData().then(() => {
      this.initBuilder();
      this.applyBoxDiscount();
      this.initPackToggle();
      this.initSubscriptions();
      this.bindAddToCart();
      this.syncPriceDisplay();
    });
    this.preloadImages();
  }

  // Fetch dynamic box data from Skio (pricing, selling plans, box ID)
  async fetchSkioData() {
    const fetchBox = async (productId) => {
      try {
        const res = await fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productPlatformId: productId }),
        });
        return await res.json();
      } catch (err) {
        console.error('Failed to fetch Skio bundle data for', productId, err);
        return null;
      }
    };

    // Fetch 24-pack (primary) and 16-pack (if UK) in parallel
    const [data24, data16] = await Promise.all([
      fetchBox(this.productId),
      this.productId16 ? fetchBox(this.productId16) : Promise.resolve(null),
    ]);

    this.skioData = data24;
    this.skioData16 = data16;
  }

  // Preload selected-state product images so thumbnails appear instantly
  preloadImages() {
    const urls = new Set(
      Array.from(this.querySelectorAll('[data-product-img]'))
        .map(el => el.dataset.productImg)
        .filter(Boolean)
    );
    urls.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }

  // Populate subscription frequency dropdown from Skio selling plans
  initSubscriptions() {
    const select = this.querySelector('select[name="subscription-picker"]');
    if (!select) return;

    const plans = Object.entries(this.skioData.DynamicBox.sellingPlanGroup)
      .map(([gid, plan]) => ({ id: gid.match(/\d+$/)[0], ...plan }));

    select.innerHTML = plans
      .map(p => `<option value="${p.id}">${p.title}</option>`)
      .join('');

    const subscribeRadio = this.querySelector('#subscribe');
    const oneTimeRadio = this.querySelector('#one-time');

    select.addEventListener('change', () => {
      if (subscribeRadio) {
        subscribeRadio.checked = true;
        subscribeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (oneTimeRadio) oneTimeRadio.checked = false;
    });
  }

  // Keep the ATC button price in sync with the selected purchase type
  syncPriceDisplay() {
    const btnPrice = this.querySelector('.price-bundle-container');
    const oneTimeEl = this.querySelector('.onetime-price-js');
    const subEl = this.querySelector('.subscription-price-js');
    const oneTimeRadio = this.querySelector('#one-time');
    const subRadio = this.querySelector('#subscribe');
    if (!btnPrice || !oneTimeEl || !subEl) return;

    const sync = () => {
      btnPrice.textContent = subRadio?.checked ? subEl.textContent : oneTimeEl.textContent;
    };

    sync();
    oneTimeRadio?.addEventListener('change', sync);
    subRadio?.addEventListener('change', sync);
  }

  // Wire up the Add to Bag button to submit the bundle to cart
  bindAddToCart() {
    const siteCart = (typeof SiteCart !== "undefined" && SiteCart._primaryInstance) || document.querySelector('site-cart');
    const atcBtn = this.querySelector('.bundle-menu .atc-btn');

    atcBtn.addEventListener('click', () => {
      const variantIds = this.items.map(item => item.id);

      // Use the correct Skio box based on active pack size
      const activePackBtn = this.querySelector('.pack-toggle-btn[aria-pressed="true"]');
      const packSize = parseInt(activePackBtn?.dataset.packSize, 10) || 24;
      const skio = packSize === 16 && this.skioData16 ? this.skioData16 : this.skioData;
      const boxId = skio?.DynamicBox?.boxId;

      const purchaseType = this.querySelector('input[name="purchase-type"]:checked')?.value;
      const sellingPlan = purchaseType === 'subscribe'
        ? this.querySelector('select[name="subscription-picker"]')?.value
        : null;

      siteCart.addBYOB(variantIds, boxId, 0, sellingPlan || undefined);
    });
  }

  // Scroll to the product carousel when an empty slot is clicked
  scrollToProducts() {
    const target = this.querySelector('.product-list-top');
    if (!target) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  // Main builder: selection state, slot rendering, button state
  initBuilder() {
    const menu = this.querySelector('.bundle-menu');
    const slots = this.querySelector('.bundle-slots');
    const atcBtn = this.querySelector('.bundle-menu .atc-btn');
    const atcBtnText = this.querySelector('.bundle-menu .atc-btn-text');
    const inputs = this.querySelector('.bundle-inputs');
    const toggle = this.querySelector('.bundle-toggle');
    const purchaseOptions = this.querySelector('.purchase-options');

    toggle.addEventListener('click', () => menu.classList.toggle('closed'));

    const render = () => {
      slots.innerHTML = '';

      // Selected items
      this.items.forEach((item, i) => {
        const slot = document.createElement('div');
        slot.className = 'selected-item-bundle';

        const img = document.createElement('img');
        img.src = item.img;
        img.alt = item.name;
        img.className = 'img-bundle-item';

        const removeBtn = document.createElement('button');
        removeBtn.setAttribute('aria-label', `Remove ${item.name} from bundle`);
        removeBtn.className = 'button-remove-item-container';
        removeBtn.innerHTML = `
          <svg class="button-remove-item" width="auto" height="auto" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.7499 0.835938L15.1641 2.25015L9.41391 8.00015L15.1641 13.7502L13.7499 15.1644L7.99991 9.41415L2.24991 15.1644L0.835693 13.7502L6.58491 8.00015L0.835693 2.25015L2.24991 0.835938L7.99991 6.58515L13.7499 0.835938Z" fill="black"/>
          </svg>
        `;
        removeBtn.addEventListener('click', () => {
          this.items.splice(i, 1);
          render();
        });

        slot.append(img, removeBtn);
        slots.appendChild(slot);
      });

      // Hidden inputs
      inputs.innerHTML = '';
      this.items.forEach(item => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.value = item.id;
        inputs.appendChild(input);
      });

      // Empty slots
      for (let i = this.items.length; i < this.maxItems; i++) {
        const empty = document.createElement('button');
        empty.type = 'button';
        empty.className = 'empty-box-bundle';
        empty.setAttribute('aria-label', 'Select a style to add');
        empty.addEventListener('click', () => this.scrollToProducts());

        const img = document.createElement('img');
        img.src = this.dataset.emptyBoxUrl;
        img.className = 'img-bundle-item';
        img.alt = 'Empty slot';
        empty.appendChild(img);

        slots.appendChild(empty);
      }

      // Button state
      const isFull = this.items.length === this.maxItems;
      const remaining = this.maxItems - this.items.length;

      atcBtn.disabled = !isFull;
      atcBtn.setAttribute('aria-disabled', !isFull);
      atcBtn.classList.toggle('opacity-60', !isFull);
      purchaseOptions?.classList.toggle('hidden', !isFull);

      if (isFull) {
        atcBtnText.textContent = 'Add to Bag';
        atcBtn.setAttribute('aria-label', 'Add bundle to bag');
      } else {
        const word = remaining === 1 ? 'Style' : 'Styles';
        atcBtnText.textContent = `Pick ${remaining} ${word}`;
        atcBtn.setAttribute('aria-label', `Pick ${remaining} ${word.toLowerCase()}`);
      }
    };

    // Add-to-bundle buttons
    this.querySelectorAll('.add-to-bundle-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (this.items.length >= this.maxItems) return;
        this.items.push({
          id: btn.dataset.byobVariantId,
          name: btn.dataset.productName,
          img: btn.dataset.productImg,
        });
        render();
      });
    });

    this._render = render;
    render();
  }

  // Read Skio box discount tiers and update displayed prices (UK only)
  // Skio returns fixedDiscountTier as { "6": "2" } — keys are quantity thresholds,
  // values are discount amounts in currency units (e.g. "2" = £2)
  applyBoxDiscount() {
    const unitPrice = parseInt(this.dataset.unitPrice, 10);
    if (!unitPrice) return;

    // Find best matching discount from a fixedDiscountTier object
    const findDiscount = (tierObj, count) => {
      if (!tierObj) return 0;
      let best = 0;
      let bestQty = 0;
      for (const [qty, val] of Object.entries(tierObj)) {
        const q = parseInt(qty, 10);
        if (q <= count && q > bestQty) {
          bestQty = q;
          best = parseFloat(val) || 0;
        }
      }
      return best;
    };

    const symbol = (this.dataset.price24 || '£').replace(/[\d.,\s]/g, '');
    const fmt = (pence) => symbol + (pence / 100).toFixed(2).replace(/\.?0+$/, '');

    // 24-pack uses primary Skio box — only overwrite if Skio returned tiers
    const tiers24 = this.skioData?.DynamicBox?.fixedDiscountTier;
    if (tiers24 && Object.keys(tiers24).length) {
      this.dataset.price24 = fmt(unitPrice * 6 - findDiscount(tiers24, 6) * 100);
    }

    // 16-pack uses its own Skio box (separate product)
    const tiers16 = this.skioData16?.DynamicBox?.fixedDiscountTier;
    if (tiers16 && Object.keys(tiers16).length) {
      this.dataset.price16 = fmt(unitPrice * 4 - findDiscount(tiers16, 4) * 100);
    }

    // Update the currently visible price
    const container = this.querySelector('.price-bundle-container');
    const active = this.querySelector('.pack-toggle-btn[aria-pressed="true"]');
    if (container) container.textContent = this.dataset[`price${active?.dataset.packSize || '24'}`];
  }

  // Pack size toggle (UK only: 24-pack ↔ 16-pack)
  initPackToggle() {
    const toggleBtns = this.querySelectorAll('.pack-toggle-btn');
    if (!toggleBtns.length) return;

    const selectHeading = this.querySelector('[data-select-heading]');
    const priceContainer = this.querySelector('.price-bundle-container');

    toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.getAttribute('aria-pressed') === 'true') return;

        const size = parseInt(btn.dataset.packSize, 10);

        toggleBtns.forEach(b => {
          const isActive = b === btn;
          b.setAttribute('aria-pressed', String(isActive));
          if (isActive) {
            b.classList.add('bg-white', 'shadow-sm', 'font-medium');
            b.classList.remove('text-black/50');
          } else {
            b.classList.remove('bg-white', 'shadow-sm', 'font-medium');
            b.classList.add('text-black/50');
          }
        });

        this.maxItems = size === 24 ? 6 : 4;

        if (this.items.length > this.maxItems) {
          this.items = this.items.slice(0, this.maxItems);
        }

        if (selectHeading) selectHeading.textContent = `Select Any ${this.maxItems} Styles`;

        if (priceContainer && this.dataset[`price${size}`]) {
          priceContainer.textContent = this.dataset[`price${size}`];
        }

        this._render();
      });
    });
  }
}

customElements.define('bundle-builder', BundleBuilder);
